/**
 * [WebSocket 관리자] - Spring Boot 백엔드와의 실시간 통신 클라이언트
 *
 * STOMP 유사 JSON 프로토콜을 사용하는 네이티브 WebSocket 클라이언트.
 * JWT를 URL이 아닌 AUTH 메시지로 전송하여 서버 로그/브라우저 기록 노출을 방지한다.
 *
 * 프로토콜:
 *   클라이언트 → 서버:
 *     { command: "AUTH", token: "..." }          // 인증
 *     { command: "SUBSCRIBE", destination: "/topic/..." }  // 구독
 *     { command: "SEND", destination: "/app/...", body: { ... } }  // 메시지 전송
 *     { command: "UNSUBSCRIBE", destination: "/topic/..." }  // 구독 해제
 *     { command: "PING" }                         // 하트비트(keepalive)
 *
 *   서버 → 클라이언트:
 *     { destination: "/topic/...", body: { ... } }  // 메시지 수신
 *     { command: "PONG" }                          // 하트비트 응답
 *
 * 기능: 지수 백오프 자동 재연결(영구 포기 없음), 하트비트 keepalive + half-open 감지,
 *       네트워크 복귀(online)·탭 복귀(visibilitychange) 시 즉시 재연결, 토픽 기반 구독.
 */

type MessageHandler = (data: unknown) => void;
type ConnectionStateHandler = (connected: boolean) => void;
type TokenProvider = () => string | null | Promise<string | null>;

/** 최대 재연결 지연 시간 (밀리초) */
const MAX_RECONNECT_DELAY = 30_000;

/** 초기 재연결 지연 시간 (밀리초) */
const INITIAL_RECONNECT_DELAY = 1_000;

/** 하트비트(PING) 전송 주기 (밀리초). 프록시/NAT idle timeout보다 충분히 짧게. */
const HEARTBEAT_INTERVAL_MS = 25_000;

/**
 * 서버 메시지(PONG 포함)가 이 시간 동안 한 번도 없으면 연결이 죽은(half-open) 것으로 보고
 * 강제 재연결한다. PING 주기의 2~3배로 잡아 일시적 지연에 오탐하지 않게 한다.
 */
const HEARTBEAT_STALE_MS = 70_000;

/**
 * 지수 백오프 지연을 equal-jitter 로 계산한다(최대값으로 캡) — P2-C.
 *
 * 캡(capped=min(initial·2^attempts, max))을 먼저 구한 뒤 [capped/2, capped] 범위로 균등 지터한다:
 *   delay = round(capped·0.5 + random()·capped·0.5)
 *
 * 다수 클라이언트가 서버 복귀 순간 동시에 재연결하며 몰리는 thundering-herd 를 완화한다.
 * 캡 base 를 먼저 적용하므로 반환은 항상 ≤ maxMs 이며(캡 초과 불가), 캡에 몰리지도 않는다.
 * 영구 포기는 하지 않으므로 attempts 가 커져도 지연은 [max/2, max] 로 수렴한다.
 */
export function computeReconnectDelayMs(
  attempts: number,
  initialMs: number = INITIAL_RECONNECT_DELAY,
  maxMs: number = MAX_RECONNECT_DELAY,
  random: () => number = Math.random,
): number {
  const capped = Math.min(initialMs * 2 ** attempts, maxMs);
  return Math.round(capped * 0.5 + random() * capped * 0.5);
}

/** 마지막 서버 메시지 이후 staleMs를 초과했으면 연결이 죽은 것으로 판단한다. */
export function isConnectionStale(
  now: number,
  lastServerMessageAt: number,
  staleMs: number = HEARTBEAT_STALE_MS,
): boolean {
  return now - lastServerMessageAt > staleMs;
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, Set<MessageHandler>> = new Map();
  private connectionListeners: Set<ConnectionStateHandler> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private url: string = '';
  private token: string | null = null;
  private intentionalDisconnect = false;
  private tokenProvider: TokenProvider | null = null;
  private authenticated = false;
  // 연속 인증 실패 횟수. AUTH_OK 로 0 리셋. 재연결 백오프를 승격시켜(onopen 이 reconnectAttempts 를
  // 0 으로 리셋해도) 시크릿 회전/정지계정에서 1s 핫루프 대신 지수 백오프가 걸리게 한다(P2-B).
  private consecutiveAuthFailures = 0;

  // 하트비트 상태
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastServerMessageAt = 0;
  private networkListenersAttached = false;

  constructor() {
    this.setupNetworkListeners();
  }

  // ---- 연결 --------------------------------------------------------

  connect(url: string, token: string): void {
    // 중복 연결 방지
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.url = url;
    this.token = token;
    this.intentionalDisconnect = false;

    try {
      this.ws = new WebSocket(url);
    } catch {
      // WebSocket 생성자는 잘못된 URL 등에서 동기 예외를 던짐 — 재연결 스케줄링으로 복구
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.authenticated = false;
      this.lastServerMessageAt = Date.now();
      this.startHeartbeat();

      // 첫 번째 메시지로 토큰을 전송하여 인증 (URL에 포함하지 않음)
      if (this.token) {
        this.ws!.send(JSON.stringify({ command: 'AUTH', token: this.token }));
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };

    this.ws.onclose = () => {
      this.stopHeartbeat();
      this.notifyConnectionState(false);
      if (!this.intentionalDisconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onerror 후 onclose가 호출되므로, 재연결은 onclose에서 처리
    };
  }

  /** 저장된 토큰을 업데이트한다. 토큰 갱신 시 호출된다. */
  updateToken(token: string): void {
    this.token = token;
  }

  /**
   * 재연결 시 새 토큰을 제공하는 콜백을 설정한다.
   * 매 재연결 시도 전에 호출되며, 동기/비동기 모두 지원한다.
   */
  setTokenProvider(provider: TokenProvider): void {
    this.tokenProvider = provider;
  }

  /** WebSocket 연결을 정상 종료하고 대기 중인 재연결 시도를 취소한다. */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.authenticated = false;
    this.clearReconnectTimer();
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.notifyConnectionState(false);
  }

  // ---- 구독 관리 -----------------------------------------------------

  /**
   * 지정된 토픽/목적지에 메시지를 구독한다.
   *
   * @param topic    구독할 목적지 (예: `/topic/meeting/123/chat`)
   * @param handler  파싱된 메시지 본문으로 호출되는 콜백
   * @returns        구독 해제 함수
   */
  subscribe(topic: string, handler: MessageHandler): () => void {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());

      // 연결 및 인증 완료 상태이면 서버에 구독 요청 전송
      if (this.isConnected() && this.authenticated) {
        this.sendFrame('SUBSCRIBE', topic);
      }
    }

    this.subscriptions.get(topic)!.add(handler);

    // 구독 해제 함수 반환
    return () => {
      const handlers = this.subscriptions.get(topic);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.subscriptions.delete(topic);
          // 서버에 구독 해제 알림
          if (this.isConnected() && this.authenticated) {
            this.sendFrame('UNSUBSCRIBE', topic);
          }
        }
      }
    };
  }

  // ---- 메시지 전송 -----------------------------------------------------------

  /** 지정된 목적지로 메시지를 전송한다 (예: `/app/chat/meeting/123`). */
  send(destination: string, body: unknown): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketManager] Cannot send - not connected');
      return;
    }

    const frame = JSON.stringify({
      command: 'SEND',
      destination,
      body,
    });

    this.ws!.send(frame);
  }

  // ---- 연결 상태 리스너 ----------------------------------------

  /**
   * 연결 상태 변경 시 호출되는 리스너를 등록한다.
   * 리스너 제거 함수를 반환한다.
   */
  onConnectionChange(handler: ConnectionStateHandler): () => void {
    this.connectionListeners.add(handler);
    return () => {
      this.connectionListeners.delete(handler);
    };
  }

  /** WebSocket이 현재 열려 있으면 true를 반환한다. */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // ---- 내부 메서드 -----------------------------------------------------------

  /**
   * 수신된 WebSocket 메시지의 JSON 프레임을 파싱하고
   * 해당 목적지를 구독한 모든 핸들러에 본문을 전달한다.
   */
  private handleMessage(event: MessageEvent): void {
    // 어떤 서버 메시지든 수신하면 "살아있음"으로 간주 (half-open 감지 기준 갱신)
    this.lastServerMessageAt = Date.now();

    let frame: { destination?: string; body?: unknown; command?: string; code?: string };

    try {
      frame = JSON.parse(event.data as string);
    } catch {
      console.warn('[WebSocketManager] Failed to parse incoming message:', event.data);
      return;
    }

    const { destination, body } = frame;

    // 하트비트 응답 — keepalive 확인용. 추가 처리 없음.
    if (frame.command === 'PONG') {
      return;
    }

    // 서버의 AUTH 확인 응답 처리
    if (frame.command === 'AUTH_OK' || destination === '/system/auth-ok') {
      this.authenticated = true;
      this.consecutiveAuthFailures = 0; // 인증 성공 → 백오프 승격 리셋
      this.notifyConnectionState(true);

      // 인증 완료 후 대기 중인 구독 요청 일괄 전송
      for (const dest of this.subscriptions.keys()) {
        this.sendFrame('SUBSCRIBE', dest);
      }
      return;
    }

    // AUTH 실패 신호: 서버가 명시적으로 AUTH_FAIL 을 보냈거나(신서버), 아직 인증 전인데 ERROR 를
    // 받은 경우(구서버 — AUTH_FAIL 미지원). 세션을 강제 재연결로 되살린다(시크릿 회전/토큰 만료 시
    // 수동 새로고침 없이 복구). consecutiveAuthFailures 로 백오프를 키워 정지계정 1s 핫루프를 막는다.
    //
    // ⚠️ 배포 순서는 CLIENT-FIRST(이 클라이언트 먼저 배포): 구서버+신클라는 안전하지만
    //    신서버(1008 close)+구클라는 close 를 1s 재연결로 처리해 핫루프가 된다.
    if (frame.command === 'AUTH_FAIL' || (frame.command === 'ERROR' && !this.authenticated)) {
      this.consecutiveAuthFailures += 1;
      console.warn(
        '[WebSocketManager] AUTH failed (%s) — forcing reconnect (attempt %d)',
        frame.command === 'AUTH_FAIL' ? (frame.code ?? 'AUTH_FAIL') : 'ERROR/unauthenticated',
        this.consecutiveAuthFailures,
      );
      this.forceReconnect();
      return;
    }

    if (!destination) return;

    const handlers = this.subscriptions.get(destination);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(body);
        } catch (err) {
          console.error('[WebSocketManager] Handler error:', err);
        }
      });
    }
  }

  /** 서버에 제어 프레임(SUBSCRIBE / UNSUBSCRIBE)을 전송한다. */
  private sendFrame(command: string, destination: string): void {
    if (!this.isConnected()) return;

    const frame = JSON.stringify({ command, destination });
    this.ws!.send(frame);
  }

  /** 모든 연결 상태 리스너에 변경을 알린다. */
  private notifyConnectionState(connected: boolean): void {
    this.connectionListeners.forEach((handler) => {
      try {
        handler(connected);
      } catch (err) {
        console.error('[WebSocketManager] Connection listener error:', err);
      }
    });
  }

  // ---- 하트비트 (keepalive + half-open 감지) ----------------------------------

  /** 주기적 PING으로 연결을 살리고, 서버 응답 부재 시 강제 재연결한다. */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastServerMessageAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected()) return;

      // half-open 감지: 일정 시간 서버 메시지(또는 PONG)가 없으면 죽은 연결로 보고 재연결
      if (isConnectionStale(Date.now(), this.lastServerMessageAt)) {
        console.warn('[WebSocketManager] Heartbeat stale — forcing reconnect');
        this.forceReconnect();
        return;
      }

      try {
        this.ws!.send(JSON.stringify({ command: 'PING' }));
      } catch {
        // 죽은 소켓에 send → 강제 재연결로 복구
        this.forceReconnect();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 죽은(half-open) 소켓을 직접 닫고 재연결을 스케줄한다.
   * onclose가 오지 않을 수 있으므로 핸들러를 떼고 수동으로 정리한다.
   */
  private forceReconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null; // 중복 스케줄 방지 (아래에서 직접 스케줄)
      this.ws.onerror = null;
      try {
        this.ws.close();
      } catch {
        /* already closed */
      }
      this.ws = null;
    }
    this.authenticated = false;
    this.notifyConnectionState(false);
    if (!this.intentionalDisconnect) {
      this.scheduleReconnect();
    }
  }

  // ---- 네트워크/가시성 복귀 시 즉시 재연결 ------------------------------------

  /**
   * 브라우저 `online`·`visibilitychange(visible)` 이벤트에 재연결을 건다.
   * 노트북 절전→복귀, 와이파이 재연결 등으로 장시간 끊겼다가 돌아온 경우,
   * 백오프 누적으로 인한 지연/포기 없이 즉시 빠르게 복구한다.
   */
  private setupNetworkListeners(): void {
    if (typeof window === 'undefined' || this.networkListenersAttached) return;
    this.networkListenersAttached = true;

    const reconnectNow = () => {
      if (this.intentionalDisconnect || this.isConnected()) return;
      if (!this.url || !this.token) return; // 아직 한 번도 연결한 적 없음
      this.reconnectAttempts = 0; // 빠른 재시도로 리셋 (영구 포기 방지의 핵심)
      this.clearReconnectTimer();
      this.scheduleReconnect();
    };

    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reconnectNow();
    });
  }

  /**
   * 지수 백오프로 재연결을 예약한다.
   *
   * 영구 포기하지 않는다 — 지연은 MAX_RECONNECT_DELAY로 캡되며, 서버가 돌아오면 회복한다.
   * (회의 클라이언트는 회의가 끝날 때까지 실시간 채널이 살아 있어야 하므로 의도된 동작이다.)
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    if (this.intentionalDisconnect || !this.url || !this.token) return;

    // 인증 실패 누적을 백오프에 반영한다: onopen 이 reconnectAttempts 를 0 으로 리셋해도
    // consecutiveAuthFailures 가 유지돼(AUTH_OK 전까지) 시크릿 회전/정지계정 1s 핫루프를 막는다.
    const attempts = Math.max(this.reconnectAttempts, this.consecutiveAuthFailures);
    const delay = computeReconnectDelayMs(attempts);
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(async () => {
      if (this.intentionalDisconnect || !this.url) return;

      // 토큰 제공자가 있으면 새 토큰을 가져온다 (비동기 토큰 갱신 지원)
      if (this.tokenProvider) {
        try {
          const freshToken = await Promise.resolve(this.tokenProvider());
          if (freshToken) {
            this.token = freshToken;
          } else {
            // 토큰 제공자가 null을 반환 — 이번 시도는 건너뛰되, 다음 백오프로 계속 재시도
            console.warn('[WebSocketManager] Token provider returned null, will retry');
            this.scheduleReconnect();
            return;
          }
        } catch (err) {
          // 토큰 갱신 실패(일시적 네트워크 등) — 포기하지 않고 다음 백오프로 재시도
          console.warn('[WebSocketManager] Token provider failed, will retry:', err);
          this.scheduleReconnect();
          return;
        }
      }

      if (this.token) {
        this.connect(this.url, this.token);
      }
    }, delay);
  }

  /** 대기 중인 재연결 타이머를 해제한다. */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

/** 싱글톤 WebSocket 관리자 인스턴스 */
export const wsManager = new WebSocketManager();
